import { Link, makeStyles, Text, tokens } from "@fluentui/react-components";
import * as React from "react";
import { CardSection } from "../../components/CardSection";
import { CodeBlock } from "../../components/CodeBlock";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "2rem clamp(1rem, 5vw, 4rem)",
        width: "80%",
        margin: "0 auto",
        maxWidth: "1400px",
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: "12px",
        boxShadow: tokens.shadow16,
        padding: "1.5rem",
    },
    ul: {
        paddingLeft: "1.25rem",
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
    }
});

export const csprojSnippet = `<Project Sdk="Microsoft.NET.Sdk">
  <PropertyGroup>
    <TargetFramework>net472</TargetFramework>
    <AssemblyTitle>Publisher.Product.Package</AssemblyTitle>
    <Product>Publisher.Product.Package</Product>
    <Copyright>Copyright © 2026</Copyright>
  </PropertyGroup>
  <ItemGroup>
    <PackageReference Include="Microsoft.PowerApps.MSBuild.PDPackage" Version="1.*">
      <PrivateAssets>all</PrivateAssets>
      <IncludeAssets>runtime; build; native; contentfiles; analyzers; buildtransitive</IncludeAssets>
    </PackageReference>
    <PackageReference Include="Microsoft.CrmSdk.XrmTooling.CoreAssembly" Version="9.1.0.79" PrivateAssets="all" />
    <PackageReference Include="Microsoft.CrmSdk.XrmTooling.PackageDeployment" Version="9.1.0.74" PrivateAssets="all" />
  </ItemGroup>
  <ItemGroup>
    <Reference Include="System.ComponentModel.Composition" />
  </ItemGroup>
  <ItemGroup>
    <PdSolution Include="../../IanusGuard_managed.zip" />
    <PdSolution Include="../../ProductSolution_managed.zip" />
  </ItemGroup>
</Project>`;

export const Id = "378b7f5b-2375-443c-832e-8e1f5b742283";
export const Title = "Shipping";
export const Subtitle = "Find out about how to ship your products with Ianus Guard";
export const Description = "Description on various strategies for bundling and shipping your protected products";
export const Keywords = ["solution", "package", "bundle", "import", "export", "distribution", "delivery"];

export const Component: React.FC = React.memo(() => {
    const styles = useStyles();

    return (
        <div className={styles.container}>
            <CardSection title="Various strategies for bundling and shipping your protected products">
                <Text>
                    There are two options for shipping protected products:
                </Text>
                <ul className={styles.ul}>
                    <li>Require customers to install the official Ianus Guard solution first</li>
                    <li>Bundle Ianus Guard with your product package</li>
                </ul>
                <Text>
                    The first option is a good fit for simple distribution patterns where you deliver a single solution file directly.
                    Customers install Ianus Guard before installing your product.
                </Text>
                <Text>
                    The second option is recommended for more advanced distribution scenarios, especially for Microsoft AppSource deployments.
                    Bundling Ianus Guard inside your package simplifies installation by importing one package that contains all required components.
                </Text>
            </CardSection>

            <CardSection title="Require customers to install the official Ianus Guard solution first">
                <Text>
                    With this approach, your product package does not include Ianus Guard.
                    Customers must install the official Ianus Guard solution first, then install your product.
                </Text>
                <Text>
                    Customers can install Ianus Guard from <Link href="https://marketplace.microsoft.com/en-us/product/saas/ianuasoftware.ianus_guard?tab=overview">Microsoft AppSource</Link>{" "}
                    or download it from our <Link href="https://github.com/Ianua-Software/Ianus-Clients/blob/main/solutions/IanusGuard_managed.zip" target="_blank">GitHub repository</Link>.
                </Text>
                <Text>
                    After Ianus Guard is installed, your product can be installed and used normally.
                </Text>
            </CardSection>

            <CardSection title="Bundle the Ianus Guard solution with your product package">
                <Text>
                    With this approach, your package deployer bundle includes the Ianus Guard solution together with your custom solution.
                    Use the managed Ianus Guard solution from our <Link href="https://github.com/Ianua-Software/Ianus-Clients/blob/main/solutions/IanusGuard_managed.zip" target="_blank">GitHub repository</Link>.
                </Text>
                <Text>
                    When the package is imported, contained solutions are installed in the order specified in the package deployer .csproj file.
                    Make sure Ianus Guard appears before your custom solution so the dependency is fulfilled.
                </Text>
                <CodeBlock code={csprojSnippet} language="xml" />
                <Text>
                    <strong>Updating</strong>:
                    When different publishers bundle different versions of Ianus Guard, the installation process handles it safely.
                </Text>
                <ul className={styles.ul}>
                    <li>The highest bundled Ianus Guard version wins.</li>
                    <li>If a lower version is installed, it is updated to the higher version.</li>
                    <li>If a higher version is already installed, the bundled Ianus Guard solution is skipped.</li>
                </ul>
                <Text>
                    This makes it possible to keep existing bundles working even when a newer Ianus Guard release is available.
                    Still, it is a good idea to review new releases regularly for improvements and fixes.
                </Text>
            </CardSection>
        </div>
    );
});
